import React from 'react';
import { useTranslation } from 'react-i18next';
import type { AgentActivityState } from '../../hooks/useAgentAsk';
import type { SuggestedAction } from '../../types';
import { AgentActivity } from '../AgentActivity';
import type { ChapterPinHandlers } from '../assistant/ChapterTemplates';
import { AnswerReceipt } from '../assistant/AnswerReceipt';
import { chapterDisplayTitle } from '../assistant/chapterGate';
import { AgentMarkdown } from '../shared/AgentMarkdown';
import { Icon } from '../shared/Icon';
import type { CardMessage } from './cards';
import { ContextEvent, ContextSwitchOffer } from './ConversationContext';
import { MessageCard } from './MessageCard';
import type { ThreadMessage } from './threadTypes';

function FollowUps({
  actions,
  onAction,
  className,
}: {
  actions?: SuggestedAction[];
  onAction: (action: SuggestedAction) => void;
  className: string;
}) {
  if (!actions?.length) return null;
  return (
    <div className="maitre-followups">
      {actions.map(action => (
        <button key={action.id} type="button" className={className} onClick={() => onAction(action)}>
          {action.label}
        </button>
      ))}
    </div>
  );
}

/** Show the scope next to a question only when it changed from the last one. */
function scopeChanges(messages: ThreadMessage[]): Set<string> {
  const changed = new Set<string>();
  let last: string | undefined;
  for (const m of messages) {
    if (m.role !== 'user' || !m.scopeKey) continue;
    if (last !== undefined && m.scopeKey !== last) changed.add(m.id);
    last = m.scopeKey;
  }
  return changed;
}

/**
 * The conversation, in both sizes. `panel` renders each card compactly under
 * its answer; `conversation` (the home) does too with `cardsInline`, each card
 * offering to open beside the chat, and otherwise links to the card that is
 * open there.
 */
export function ThreadMessages({
  messages,
  variant,
  cardsInline = false,
  openCardId = null,
  busy,
  activity,
  onAction,
  onRetry,
  pinsFor,
  onOpenCard,
  onSwitchContext,
}: {
  messages: ThreadMessage[];
  variant: 'panel' | 'conversation';
  /** `conversation`: the card under its answer, not a link to it. */
  cardsInline?: boolean;
  /** `conversation`: the card open beside the chat. */
  openCardId?: string | null;
  busy: boolean;
  activity: AgentActivityState;
  onAction: (action: SuggestedAction) => void;
  onRetry: (messageId: string) => void;
  pinsFor?: (message: CardMessage, index: number) => ChapterPinHandlers;
  onOpenCard?: (messageId: string) => void;
  /** Switch the conversation's context to what an answer said it needed. */
  onSwitchContext?: (messageId: string) => void;
}) {
  const { t } = useTranslation('assistant');
  const panel = variant === 'panel';
  const changed = scopeChanges(messages);
  let cardIndex = 0;

  const lastId = messages[messages.length - 1]?.id;
  const items = messages.map(m => {
    if (m.role === 'context') {
      return <ContextEvent key={m.id} kind={m.kind} context={m.context} />;
    }
    if (m.role === 'user') {
      const chips =
        m.focus || changed.has(m.id) ? (
          <div className="maitre-msg-chips">
            {m.focus ? (
              <span className="maitre-chip is-focus">
                {t('maitre.about', { label: m.focus.label })}
              </span>
            ) : null}
            {changed.has(m.id) && m.scopeLabel ? (
              <span className="maitre-chip">{t('maitre.scope', { label: m.scopeLabel })}</span>
            ) : null}
          </div>
        ) : null;
      return panel ? (
        <div key={m.id} className="agent-msg agent-msg-user">
          {chips}
          <div className="agent-bubble">
            {m.text.split('\n').map((line, i) => (
              <p key={i}>{line}</p>
            ))}
          </div>
        </div>
      ) : (
        <div key={m.id} className="maitre-side-turn">
          {chips}
          <div className="maitre-side-line maitre-side-user">{m.text}</div>
        </div>
      );
    }
    if (m.error) {
      return (
        <div
          key={m.id}
          className={panel ? 'agent-msg agent-msg-assistant agent-msg-error' : 'maitre-side-line maitre-side-assistant is-error'}
        >
          <div className={panel ? 'agent-bubble' : undefined}>
            <div className="agent-error-row">
              {m.text.split('\n').map((line, i) => (
                <p key={i}>{line}</p>
              ))}
              {m.retryQuestion ? (
                <button type="button" className="agent-retry" onClick={() => onRetry(m.id)} disabled={busy}>
                  <Icon name="refresh" size={13} />
                  {t('ask.retry')}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      );
    }
    const card = m.card ? (m as CardMessage) : null;
    if (card) cardIndex += 1;
    const index = cardIndex;
    // The offer to switch stands under the latest answer only; once the
    // conversation moved on, the receipt still says what wasn't read.
    const offer =
      m.contextProposal && onSwitchContext && m.id === lastId ? (
        <ContextSwitchOffer proposal={m.contextProposal} onSwitch={() => onSwitchContext(m.id)} disabled={busy} />
      ) : null;
    const body = (
      <div className={panel ? 'agent-md' : 'maitre-md'}>
        <AgentMarkdown text={m.text} />
        {offer}
        <AnswerReceipt receipt={m.contextReceipt} receipts={m.receipts} context={m.answerContext} />
      </div>
    );
    if (panel) {
      return (
        <div key={m.id} className={`agent-msg agent-msg-assistant${m.refused ? ' agent-msg-refused' : ''}`}>
          <div className="agent-bubble">
            {body}
            {card ? <MessageCard message={card} density="compact" pins={pinsFor?.(card, index)} /> : null}
            <FollowUps actions={m.suggestedActions} onAction={onAction} className="agent-chip" />
          </div>
        </div>
      );
    }
    return (
      <div key={m.id} className="maitre-side-line maitre-side-assistant">
        {body}
        {card && cardsInline ? (
          <MessageCard
            message={card}
            density="compact"
            pins={pinsFor?.(card, index)}
            action={
              onOpenCard ? (
                <button type="button" className="maitre-tile-btn maitre-card-open" onClick={() => onOpenCard(card.id)}>
                  <Icon name="expand" size={13} />
                  {t('maitre.openCard')}
                </button>
              ) : null
            }
          />
        ) : card && onOpenCard ? (
          <button
            type="button"
            className={`maitre-card-link${card.id === openCardId ? ' is-on' : ''}`}
            aria-pressed={card.id === openCardId}
            onClick={() => onOpenCard(card.id)}
          >
            <Icon name="grid" size={13} />
            {chapterDisplayTitle(card.card, card.question ?? '')}
          </button>
        ) : null}
        <FollowUps actions={m.suggestedActions} onAction={onAction} className="maitre-followup" />
      </div>
    );
  });

  const working = busy || Boolean(activity.draft);
  return (
    <>
      {items}
      {working ? (
        panel ? (
          <div className="agent-msg agent-msg-assistant">
            <div className="agent-bubble">
              {busy ? <AgentActivity activity={activity} /> : null}
              {activity.draft ? (
                <div className="agent-md">
                  <AgentMarkdown text={activity.draft} />
                </div>
              ) : null}
            </div>
          </div>
        ) : (
          <>
            {busy ? (
              <div className="maitre-side-activity">
                <AgentActivity activity={activity} />
              </div>
            ) : null}
            {activity.draft ? (
              <div className="maitre-side-line maitre-side-assistant is-live">
                <div className="maitre-md">
                  <AgentMarkdown text={activity.draft} />
                </div>
              </div>
            ) : null}
          </>
        )
      ) : null}
    </>
  );
}
